import { AxiosResponse } from 'axios';

import {
    AudioDetailsResponse,
    AudioSearchResponse,
    GetSimilarAudioItems200Response,
    GetTypeaheadSuggestionsContentClassEnum,
    GraphicSearchResponse,
    SearchSimilarApiGetSimilarAudioItemsRequest,
    TypeaheadResponse,
    VideoSearchResponse,
    WebSearchApiSearchAudioRequest,
    WebSearchApiSearchGraphicRequest,
    WebSearchApiSearchVideoRequest,
} from '@videoblocks/search-gateway-client';

import BaseSearchGatewayClient from './BaseSearchGatewayClient';
import { audioRules, imageRules, similarSearchAudioRules, videoRules } from './rules';
import { ISearchGatewayClient } from './types';

export default class SearchGatewayClient extends BaseSearchGatewayClient implements ISearchGatewayClient {
    public async searchAudio(props: WebSearchApiSearchAudioRequest): Promise<AudioSearchResponse | undefined> {
        try {
            const req = this.buildRequest<WebSearchApiSearchAudioRequest>(props, audioRules);
            const response = await this.api.searchAudio(req);
            return response.data;
        } catch (error) {
            this.logError(error, props);
        }
    }

    public getSearchAudioRequest(props: WebSearchApiSearchAudioRequest): WebSearchApiSearchAudioRequest {
        return this.buildRequest<WebSearchApiSearchAudioRequest>(props, audioRules);
    }

    public async searchVideo(props: WebSearchApiSearchVideoRequest): Promise<VideoSearchResponse | undefined> {
        try {
            const req = this.buildRequest<WebSearchApiSearchVideoRequest>(props, videoRules);
            const response = await this.api.searchVideo(req);
            return response.data;
        } catch (error) {
            this.logError(error, props);
        }
    }

    public async searchImages(props: WebSearchApiSearchGraphicRequest): Promise<GraphicSearchResponse | undefined> {
        try {
            const req = this.buildRequest<WebSearchApiSearchGraphicRequest>(props, imageRules);
            const response = await this.api.searchGraphic(req);
            return response.data;
        } catch (error) {
            this.logError(error, props);
        }
    }

    public async getTypeAheadSuggestion(
        contentClass: 'video' | 'audio' | 'graphic',
        keywords?: string,
    ): Promise<TypeaheadResponse | undefined> {
        try {
            const contentClassEnum = contentClass as GetTypeaheadSuggestionsContentClassEnum;
            const response = await this.typeaheadApi.getTypeaheadSuggestions({
                contentClass: contentClassEnum,
                keywords,
            });
            return response.data;
        } catch (error) {
            this.logger.error({ contentClass, keywords, error }, 'Error fetching typeahead suggestions');
        }
    }

    public async searchSimilarAudio(
        params: SearchSimilarApiGetSimilarAudioItemsRequest,
    ): Promise<GetSimilarAudioItems200Response | undefined> {
        try {
            const req = this.buildRequest<SearchSimilarApiGetSimilarAudioItemsRequest>(params, similarSearchAudioRules);
            const results = await this.searchSimilarApi.getSimilarAudioItems(req);
            return results.data;
        } catch (error) {
            this.logError(error, params);
        }
    }

    public async getAudioDetailsByUrlId(urlId: string): Promise<AudioDetailsResponse> {
        try {
            const response = await this.detailsApi.getAudioDetailsByUrlId({
                urlId,
            });
            return response.data;
        } catch (error) {
            this.logError(error, { urlId });
            throw error;
        }
    }
}
